import React, { useEffect, useState } from "react";
import styled from "styled-components";
import useOutsideClickTrigger from "@hooks/useOutsideClickTrigger";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import CaretIconToggle from "../Icons/CaretIconToggle";

const DropdownSelectorWrapper = styled.div`
    display: block;
    font-size: ${FontSize.normal};
    position: relative;
    z-index: ${Styles.zIndexWidgetContents};
`;

const DropdownSelectorPreview = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    min-height: ${FontSize.normal};
    height: 28px;
    padding: 4px 8px;

    cursor: pointer;
    border: 0 solid ${(props) => props.theme.borderPrimary};
    border-radius: ${Styles.borderRadiusExtraSharp};
    background-color: inherit;
`;

const StyledCaretToggleIcon = styled(CaretIconToggle)`
    margin-left: 8px;
`;

const DropdownSelectorMenu = styled.div<{ visible: boolean }>`
    display: ${(props) => (props.visible ? "flex" : "none")};

    flex-direction: column;
    justify-content: flex-start;

    position: fixed;
    min-width: 250px;
    width: fit-content;

    background-color: ${(props) => props.theme.backgroundPrimary};
    box-shadow: ${(props) => props.theme.boxShadow} ${(props) => props.theme.boxShadowColor};
    border-radius: ${Styles.borderRadiusSharp};
`;

const DropdownSelectorContents = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    max-height: 360px;
    overflow-y: scroll;

    border-radius: ${Styles.borderRadiusSharp};
`;

const DropdownMenuFixedFooter = styled.div`
    height: 50px;
    border-bottom-left-radius: ${Styles.borderRadiusSharp};
    border-bottom-right-radius: ${Styles.borderRadiusSharp};
    overflow: hidden;
    border-top: 1px solid lightgrey;

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
        * {
            background-color: ${(props) => props.theme.hoverPrimary};
        }
    }
`;

interface LifeAtDropdownSelectorProps {
    /**
     * Unique ID for the selected element.
     */
    selectedElementId: string | undefined;
    /**
     * Display name for the selected element.
     */
    selectedElementName: string;
    /**
     * LifeAtDropdownSelectorElement components.
     */
    children: React.ReactNode;
    /**
     * A button to be fixed to the bottom of the scrollable dropdown container
     * so that the button doesn't scroll when the contents of the dropdown do.
     */
    fixedBottomButton?: JSX.Element;
    /**
     * To keep dropdown menu open when selected element changes
     */
    stayOpenOnSelect?: boolean;
    className?: string;
    /**
     * Icon to display in place of selected element
     */
    icon?: JSX.Element;
}
export default function LifeAtDropdownSelector(props: LifeAtDropdownSelectorProps) {
    // NOTE: cannot use useOutsideClickTrigger in this component if it is used
    //  in a Widget component, as the Widget already has bound event listeners for mousedown.
    //  Instead, we require click to open the selector, and then auto-close the dropdown if the
    //  mouse leaves the dropdown area.
    const [isDropdownExpanded, setIsDropdownExpanded] = useState<boolean>(false);
    useEffect(() => {
        // Close the dropdown if different element is selected.
        if (!props.stayOpenOnSelect) {
            setIsDropdownExpanded(false);
        }
    }, [props.selectedElementId]);

    const selectorRef = useOutsideClickTrigger(() => setIsDropdownExpanded(false));

    const toggleMenuDropdown = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDropdownExpanded(!isDropdownExpanded);
    };

    return (
        <DropdownSelectorWrapper className={`dropdown-selector-wrapper ${props.className}`} ref={selectorRef}>
            <DropdownSelectorPreview className={`dropdown-selector-preview`} onClick={toggleMenuDropdown}>
                {props.icon || props.selectedElementName}

                <StyledCaretToggleIcon showOpen={isDropdownExpanded} />
            </DropdownSelectorPreview>

            <DropdownSelectorMenu className={`dropdown-selector-contents`} visible={isDropdownExpanded}>
                <DropdownSelectorContents>{props.children}</DropdownSelectorContents>

                {props.fixedBottomButton && (
                    <DropdownMenuFixedFooter className={`dropdown-selector-footer`}>
                        {props.fixedBottomButton}
                    </DropdownMenuFixedFooter>
                )}
            </DropdownSelectorMenu>
        </DropdownSelectorWrapper>
    );
}
